/**
 * A player's own record.
 *
 * Four things, in the order somebody actually wants them: who they are and
 * what they have, how their game reads, what they have won, and what the last
 * fortnight looked like.
 *
 * The statistics are named the way poker names them — VPIP, PFR, AF, WTSD —
 * because a player who learns what those mean here can take them anywhere.
 * Each one carries the band a winning player sits in, next to their own
 * number, because a percentage on its own tells nobody anything: 34% is a fine
 * VPIP if you are on the button and a disaster if it is your average.
 *
 * And every number says how many hands it is drawn from. A room that shows
 * somebody a score after forty hands, without saying that forty hands means
 * nothing, is flattering them rather than helping them.
 */

import { useCallback, useEffect, useState } from 'react';

import { Avatar } from './avatar.tsx';
import { SiteHeader } from './Shell.tsx';
import { api } from '../server.ts';

interface SkillNote {
  stat: string;
  verdict: 'good' | 'high' | 'low';
  text: string;
}

interface Profile {
  accountId: string;
  displayName: string;
  email: string;
  emailVerified: boolean;
  memberSince: string;
  balance: number;
  cash: {
    hands: number;
    net: number;
    bbPer100: number;
    biggestPot: number;
    handsWon: number;
    tablesPlayed: number;
  };
  tournaments: {
    played: number;
    won: number;
    cashed: number;
    buyIns: number;
    prizes: number;
    roi: number | null;
    bestPlace: number | null;
  };
  skills: {
    hands: number;
    vpip: number | null;
    pfr: number | null;
    aggression: number | null;
    wentToShowdown: number | null;
    wonAtShowdown: number | null;
    score: number | null;
    confidence: 'none' | 'thin' | 'fair' | 'solid';
    notes: SkillNote[];
  };
  trophies: { kind: string; title: string; detail: string; when: string | null }[];
  recent: { day: string; hands: number; net: number }[];
}

/** The bands the server scores against, repeated here so a player can see them. */
const BANDS: Record<string, { low: number; high: number; label: string; of: string }> = {
  vpip: { low: 20, high: 30, label: 'VPIP', of: 'دست‌هایی که بازی می‌کنید' },
  pfr: { low: 15, high: 24, label: 'PFR', of: 'رِیز پیش از فلاپ' },
  aggression: { low: 1.6, high: 3.5, label: 'AF', of: 'شرط و رِیز در برابر کال' },
  wentToShowdown: { low: 22, high: 32, label: 'WTSD', of: 'رفتن تا شودان' },
};

const CONFIDENCE: Record<string, string> = {
  none: 'هنوز دست کافی بازی نکرده‌اید — این اعداد چیزی نمی‌گویند',
  thin: 'نمونه‌ی کوچک — این اعداد هنوز نوسان دارند',
  fair: 'نمونه‌ی متوسط — تصویر کلی قابل اعتماد است',
  solid: 'نمونه‌ی کافی — این اعداد بازی شما را توصیف می‌کنند',
};

export function Profile({ session }: { session: string | null }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (session === null) {
      setError('ابتدا وارد شوید.');
      return;
    }
    try {
      const response = await fetch(api('/api/profile'), {
        headers: { authorization: `Bearer ${session}` },
        cache: 'no-store',
      });
      if (!response.ok) {
        setError(response.status === 401 ? 'نشست شما منقضی شده. دوباره وارد شوید.' : `خطا ${response.status}`);
        return;
      }
      setProfile((await response.json()) as Profile);
      setError(null);
    } catch {
      setError('سرور در دسترس نیست.');
    }
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) {
    return (
      <div className="lobby">
        <h1>Profile</h1>
        <div className="error">{error}</div>
        <p className="sub">
          <a href="/">بازگشت به اتاق</a>
        </p>
      </div>
    );
  }
  if (profile === null) return <div className="lobby"><p className="sub">در حال خواندن…</p></div>;

  const { cash, tournaments, skills } = profile;

  return (
    <>
      <SiteHeader here="/profile" />
      <div className="page profile">
      {/* Who, and what they are holding. */}
      <header className="p-head">
        <Avatar name={profile.displayName} className="p-face" />
        <div className="p-who">
          <h1>{profile.displayName}</h1>
          <p className="sub">
            <span className="lat">{profile.email}</span>
            {profile.emailVerified ? (
              <span className="tag good">تأیید شده</span>
            ) : (
              <span className="tag">تأیید نشده</span>
            )}
          </p>
          <p className="sub quiet">عضو از {formatDay(profile.memberSince)}</p>
        </div>
        <div className="p-wallet">
          <span className="lat-ui">Balance</span>
          <b className="num lat-ui">{profile.balance.toLocaleString('en-US')}</b>
        </div>
      </header>

      {/* The score, and immediately after it how much it is worth. */}
      <section className="p-score-row">
        <div className="p-score">
          <Dial value={skills.score} />
          <div>
            <div className="p-score-label lat-ui">Player Score</div>
            <p className="hint">{CONFIDENCE[skills.confidence]}</p>
            <p className="hint quiet">
              بر پایه‌ی {skills.hands.toLocaleString('en-US')} دست. این امتیاز رتبه‌بندی در برابر
              بازیکنان دیگر نیست — می‌سنجد که آمار شما چقدر به محدوده‌ی بازیکنان برنده نزدیک است.
            </p>
          </div>
        </div>

        <div className="p-cards">
          <Stat label="Cash Net" value={signed(cash.net)} tone={cash.net > 0 ? 'up' : cash.net < 0 ? 'down' : undefined} />
          <Stat label="BB / 100" value={cash.bbPer100.toFixed(1)} tone={cash.bbPer100 > 0 ? 'up' : cash.bbPer100 < 0 ? 'down' : undefined} />
          <Stat label="Hands" value={cash.hands.toLocaleString('en-US')} />
          <Stat
            label="Hands Won"
            value={cash.hands === 0 ? '—' : `${Math.round((cash.handsWon / cash.hands) * 100)}%`}
          />
          <Stat label="Biggest Pot" value={cash.biggestPot.toLocaleString('en-US')} />
          <Stat label="Tables" value={String(cash.tablesPlayed)} />
        </div>
      </section>

      {/* The game itself. */}
      <h2 className="section lat-ui">Your Game</h2>
      <div className="p-skills">
        {(['vpip', 'pfr', 'aggression', 'wentToShowdown'] as const).map((key) => {
          const band = BANDS[key]!;
          const value = skills[key];
          const note = skills.notes.find((n) => n.stat === band.label);
          return (
            <div key={key} className={`p-skill ${note?.verdict ?? 'none'}`}>
              <div className="p-skill-head">
                <span className="lat-ui name">{band.label}</span>
                <b className="num lat-ui">
                  {value === null ? '—' : key === 'aggression' ? value.toFixed(1) : `${value}%`}
                </b>
              </div>
              <div className="p-skill-of">{band.of}</div>
              <Band low={band.low} high={band.high} value={value} scale={key === 'aggression' ? 6 : 60} />
              {note !== undefined && <p className="hint">{note.text}</p>}
            </div>
          );
        })}
      </div>

      {/* What they have won. */}
      <h2 className="section lat-ui">Trophy Cabinet</h2>
      {profile.trophies.length === 0 ? (
        <p className="sub quiet">
          هنوز چیزی اینجا نیست. اولین جامتان با بردن یک تورنمنت می‌آید.
        </p>
      ) : (
        <div className="p-trophies">
          {profile.trophies.map((trophy, index) => (
            <div key={index} className={`p-trophy ${trophy.kind}`}>
              <span className="p-trophy-mark" aria-hidden="true">
                {trophy.kind === 'gold' ? '★' : trophy.kind === 'silver' ? '☆' : trophy.kind === 'bronze' ? '☆' : '◆'}
              </span>
              <div>
                <div className="p-trophy-title lat-ui">{trophy.title}</div>
                <div className="p-trophy-detail">{trophy.detail}</div>
                {trophy.when !== null && <div className="p-trophy-when lat-ui">{formatDay(trophy.when)}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="section lat-ui">Tournaments</h2>
      <div className="p-cards">
        <Stat label="Played" value={String(tournaments.played)} />
        <Stat label="Won" value={String(tournaments.won)} />
        <Stat
          label="In the Money"
          value={tournaments.played === 0 ? '—' : `${Math.round((tournaments.cashed / tournaments.played) * 100)}%`}
        />
        <Stat
          label="ROI"
          value={tournaments.roi === null ? '—' : `${tournaments.roi > 0 ? '+' : ''}${tournaments.roi}%`}
          tone={tournaments.roi === null ? undefined : tournaments.roi > 0 ? 'up' : 'down'}
        />
        <Stat label="Best Finish" value={tournaments.bestPlace === null ? '—' : `#${tournaments.bestPlace}`} />
        <Stat label="Prizes" value={tournaments.prizes.toLocaleString('en-US')} />
      </div>

      {profile.recent.length > 0 && (
        <>
          <h2 className="section lat-ui">Last Fortnight</h2>
          <Sparkline days={profile.recent} />
        </>
      )}
      </div>
    </>
  );
}

/**
 * The score as a ring rather than a number in a box.
 *
 * Empty when there is no score, and it stays visible while empty — the absence
 * of a score is information too, and hiding the dial would leave a player
 * wondering where it went rather than understanding they have not played
 * enough hands to have one.
 */
function Dial({ value }: { value: number | null }) {
  const size = 128;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = value === null ? 0 : (value / 100) * circumference;

  return (
    <svg className="p-dial" viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Score ${value ?? 0}`}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--line)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--brass)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        className="p-dial-value"
      >
        {value === null ? '—' : value}
      </text>
    </svg>
  );
}

/**
 * One statistic against the band a winning player sits in.
 *
 * The band is the whole point. A number on its own tells nobody anything — the
 * useful fact is not "your VPIP is 41%", it is "winning players are between 20
 * and 30 and you are well above it".
 */
function Band({ low, high, value, scale }: { low: number; high: number; value: number | null; scale: number }) {
  const at = (n: number): string => `${Math.max(0, Math.min(100, (n / scale) * 100))}%`;
  return (
    <div className="p-band" aria-hidden="true">
      <div className="p-band-good" style={{ insetInlineStart: at(low), width: `calc(${at(high)} - ${at(low)})` }} />
      {value !== null && <div className="p-band-you" style={{ insetInlineStart: at(value) }} />}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  /**
   * Explicitly allowed to be undefined rather than merely optional: the
   * project turns on `exactOptionalPropertyTypes`, so "may be absent" and
   * "may be present and undefined" are different promises, and every caller
   * here computes the tone and hands over whatever it got.
   */
  tone?: 'up' | 'down' | undefined;
}) {
  return (
    <div className="p-stat">
      <div className="p-stat-label lat-ui">{label}</div>
      <bdi className={`p-stat-value num lat-ui ${tone ?? ''}`}>{value}</bdi>
    </div>
  );
}

/** Fourteen bars. Not a chart so much as the shape of a run. */
function Sparkline({ days }: { days: { day: string; hands: number; net: number }[] }) {
  const peak = Math.max(1, ...days.map((d) => Math.abs(d.net)));
  return (
    <div className="p-spark">
      {days.map((day) => (
        <div key={day.day} className="p-spark-day" title={`${day.day}: ${signed(day.net)} · ${day.hands} hands`}>
          <div className="p-spark-track">
            <div
              className={`p-spark-bar ${day.net >= 0 ? 'up' : 'down'}`}
              style={{ height: `${(Math.abs(day.net) / peak) * 100}%` }}
            />
          </div>
          <span className="p-spark-label lat-ui">{day.day.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

function signed(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toLocaleString('en-US')}`;
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
